import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  List,
  ListItem,
  Chip,
  Button,
  CircularProgress,
  Pagination,
  Tabs,
  Tab,
  Fade,
} from "@mui/material";
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";
import { styled, alpha, keyframes } from "@mui/material/styles";
import useApp from "../../store/contexts/AppContext";
import Header from "../../components/Header";
import SOCKET_EVENTS from "../../config/SocketEvents";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#0a0a0a" : "#f8f9fa",
  minHeight: "100vh",
  paddingBottom: theme.spacing(8),
  position: "relative",
  "&::before": {
    content: '""',
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    height: "400px",
    background: theme.palette.mode === "dark"
      ? `radial-gradient(ellipse at top, ${alpha(
          theme.palette.primary.main,
          0.15,
        )}, transparent)`
      : `radial-gradient(ellipse at top, ${alpha(
          "#d44402",
          0.12,
        )}, ${alpha("#1976d2", 0.08)}, transparent)`,
    pointerEvents: "none",
    zIndex: 0,
  },
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  paddingTop: theme.spacing(4),
  animation: `${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1)`,
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `2px solid ${theme.palette.primary.main}`,
}));

const PageTitle = styled(Typography)(({ theme}) => ({
  fontSize: "2rem",
  fontWeight: 900,
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${theme.palette.common.white}, ${alpha(theme.palette.common.white, 0.7)})`
      : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const MarkAllButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: theme.spacing(1, 3),
  fontWeight: 700,
  fontSize: "0.875rem",
  textTransform: "none",
  backgroundColor: "transparent",
  border: `2px solid ${theme.palette.primary.main}`,
  color: theme.palette.primary.main,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
    color: "#ffffff",
    transform: "translateY(-2px)",
    boxShadow: `0 4px 16px ${alpha("#d44401", 0.4)}`,
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  "& .MuiTabs-indicator": {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: 3,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  color: "text.secondary",
  fontWeight: 600,
  fontSize: "0.9rem",
  textTransform: "none",
  minHeight: 48,
  transition: "all 0.3s ease",
  "&:hover": {
    color: "text.primary",
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
  "&.Mui-selected": {
    color: theme.palette.primary.main,
  },
}));

const NotificationsList = styled(List)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
}));

const NotificationItem = styled(ListItem)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.02)
      : alpha(theme.palette.common.black, 0.02),
  borderRadius: 12,
  padding: theme.spacing(2),
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.08)
      : alpha(theme.palette.common.black, 0.08)
  }`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  display: "block",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: "translateX(4px)",
    boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.3 : 0.1)}`,
  },
}));

const UnreadNotification = styled(NotificationItem)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const NotificationContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
}));

const NotificationText = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
}));

const NotificationTitle = styled(Typography)(({ theme }) => ({
  color: "text.primary",
  fontWeight: 600,
  fontSize: "0.95rem",
  marginBottom: theme.spacing(0.5),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flexWrap: "wrap",
}));

const NotificationMessage = styled(Typography)(({ theme }) => ({
  color: "text.secondary",
  fontSize: "0.85rem",
  lineHeight: 1.5,
  marginBottom: theme.spacing(0.75),
}));

const NotificationTime = styled(Typography)(({ theme }) => ({
  color: "text.disabled",
  fontSize: "0.75rem",
}));

const PriorityChip = styled(Chip)(({ theme }) => ({
  height: 20,
  fontSize: "0.65rem",
  fontWeight: 700,
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(8, 2),
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.02)
      : alpha(theme.palette.common.black, 0.02),
  borderRadius: 12,
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.08)
      : alpha(theme.palette.common.black, 0.08)
  }`,
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(6),
}));

const NotificationList = () => {
  const {
    getUserNotificationsApiCall,
    markNotificationAsReadApiCall,
    markAllNotificationsAsReadApiCall,
    appState: { socket },
  } = useApp();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("all");

  // Fetch notifications - only depends on currentPage and filter
  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      const params = {
        pageNo: currentPage,
        pageSize: 20,
      };

      if (filter === "unread") {
        params.is_read = false;
      } else if (filter === "read") {
        params.is_read = true;
      }

      const result = await getUserNotificationsApiCall(params);

      if (result.success) {
        setNotifications(result.data.notifications);
        setTotalPages(result.data.pagination.totalPages);
      }
      setLoading(false);
    };

    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filter]);

  // Socket listener for deleted notifications
  useEffect(() => {
    if (!socket) return;

    const handleNotificationDeleted = ({ notification_id }) => {
      // Remove from local state immediately
      setNotifications((prev) =>
        prev.filter((n) => n.notification_id !== notification_id),
      );
    };

    socket.on(
      SOCKET_EVENTS.NOTIFICATION_DELETED_BROADCAST,
      handleNotificationDeleted,
    );

    return () => {
      socket.off(
        SOCKET_EVENTS.NOTIFICATION_DELETED_BROADCAST,
        handleNotificationDeleted,
      );
    };
  }, [socket]);

  const handleMarkAsRead = async (notificationId, isRead) => {
    if (isRead) return;

    const result = await markNotificationAsReadApiCall(notificationId);
    if (result.success) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.notification_id === notificationId ? { ...n, is_read: true } : n,
        ),
      );
    }
  };

  const handleMarkAllAsRead = async () => {
    const result = await markAllNotificationsAsReadApiCall();
    if (result.success) {
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    }
  };

  const handleFilterChange = (event, newValue) => {
    setFilter(newValue);
    setCurrentPage(1);
  };

  const getNotificationIcon = (type) => {
    const iconProps = { fontSize: "medium" };
    const iconMap = {
      info: <InfoIcon {...iconProps} sx={{ color: "#2a3478" }} />,
      warning: <WarningIcon {...iconProps} sx={{ color: "#f59e0b" }} />,
      success: <SuccessIcon {...iconProps} sx={{ color: "#10b981" }} />,
      error: <ErrorIcon {...iconProps} sx={{ color: "#ef4444" }} />,
    };
    return iconMap[type] || iconMap.info;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: "error",
      medium: "warning",
      low: "default",
    };
    return colors[priority] || colors.low;
  };

  return (
    <>
      <Header />
      <PageContainer>
        <ContentContainer maxWidth="lg">
          <HeaderSection>
            <PageTitle>
              <NotificationsIcon sx={{ fontSize: "2rem", color: "#d44401" }} />
              Notifications
            </PageTitle>
            {notifications.length > 0 && (
              <MarkAllButton onClick={handleMarkAllAsRead}>
                Mark All as Read
              </MarkAllButton>
            )}
          </HeaderSection>

          <StyledTabs value={filter} onChange={handleFilterChange}>
            <StyledTab label="All" value="all" />
            <StyledTab label="Unread" value="unread" />
            <StyledTab label="Read" value="read" />
          </StyledTabs>

          {loading ? (
            <LoadingContainer>
              <CircularProgress sx={{ color: "#d44401" }} />
            </LoadingContainer>
          ) : notifications.length === 0 ? (
            <Fade in timeout={400}>
              <EmptyState>
                <NotificationsIcon
                  sx={{ fontSize: 60, color: "#6c7589", mb: 2 }}
                />
                <Typography variant="h6" sx={{ color: "#9296b7", mb: 1 }}>
                  {filter === "all"
                    ? "No notifications"
                    : filter === "unread"
                      ? "No unread notifications"
                      : "No read notifications"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6c7589" }}>
                  You're all caught up!
                </Typography>
              </EmptyState>
            </Fade>
          ) : (
            <>
              <NotificationsList>
                {notifications.map((notification, index) => {
                  const ItemComponent = notification.is_read
                    ? NotificationItem
                    : UnreadNotification;

                  return (
                    <Fade
                      in
                      timeout={300}
                      style={{ transitionDelay: `${index * 50}ms` }}
                      key={notification.user_notification_id}
                    >
                      <ItemComponent
                        onClick={() =>
                          handleMarkAsRead(
                            notification.notification_id,
                            notification.is_read,
                          )
                        }
                      >
                        <NotificationContent>
                          <Box sx={{ mt: 0.25 }}>
                            {getNotificationIcon(notification.type)}
                          </Box>
                          <NotificationText>
                            <NotificationTitle>
                              {notification.title}
                              <PriorityChip
                                label={notification.priority.toUpperCase()}
                                size="small"
                                color={getPriorityColor(notification.priority)}
                              />
                              {!notification.is_read && (
                                <Chip
                                  label="NEW"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#d44401",
                                    color: "#ffffff",
                                    height: 20,
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                  }}
                                />
                              )}
                            </NotificationTitle>
                            <NotificationMessage>
                              {notification.message}
                            </NotificationMessage>
                            <NotificationTime>
                              {new Date(
                                notification.notification_created_at,
                              ).toLocaleString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </NotificationTime>
                          </NotificationText>
                        </NotificationContent>
                      </ItemComponent>
                    </Fade>
                  );
                })}
              </NotificationsList>

              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={4}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(e, page) => setCurrentPage(page)}
                    sx={{
                      "& .MuiPaginationItem-root": {
                        color: "#9296b7",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: alpha("#d44401", 0.1),
                        },
                        "&.Mui-selected": {
                          backgroundColor: "#d44401",
                          color: "#ffffff",
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </ContentContainer>
      </PageContainer>
    </>
  );
};

export default NotificationList;
